
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import type { CollectionSchedule, CollectionScheduleInstallment, PaymentMechanism, TimelineOutcome } from './CollectionScheduleTimeline';

interface FlatRow extends CollectionScheduleInstallment {
  advanceRequestId: string;
}

const STATUS_LABELS: Record<CollectionScheduleInstallment['collectionStatus'], { label: string; tone: string }> = {
  pending: { label: 'Pendiente', tone: 'text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400' },
  sent: { label: 'En cobro', tone: 'text-flux-primary bg-flux-primary/10' },
  collected: { label: 'Cobrada', tone: 'text-accent-green bg-accent-green/10' },
  abandoned: { label: 'Abandonada', tone: 'text-foreground/80 bg-muted' },
};

const MECHANISM_LABELS: Record<PaymentMechanism, string> = {
  PAC: 'PAC',
};

const EVENT_LABELS: Record<string, string> = {
  payment_succeeded: 'Pago exitoso',
  payment_failed: 'Pago fallido',
  PAC_MANUAL_COLLECTED: 'PAC',
  FLUX_ABANDONED: 'Abandonado por FLUX',
};

const formatEventLabel = (tipo: string) => EVENT_LABELS[tipo] ?? tipo;

const OUTCOME_VISUAL: Record<TimelineOutcome, { icon: string; tone: string }> = {
  success: { icon: 'check_circle', tone: 'text-accent-green' },
  failure: { icon: 'error', tone: 'text-red-500' },
  abandoned: { icon: 'do_not_disturb_on', tone: 'text-slate-500' },
  info: { icon: 'info', tone: 'text-slate-400' },
};

/**
 * Mecanismo a mostrar al comercio. Prioridad:
 *   1) `mechanism` (sólo PAC, cuando admin cerró manualmente la cuota tras cobro PAC)
 *   2) `lupsPaymentMethod` (string libre que reporta Lups: PAT, PAC, credit_card, …)
 *   3) "—" si la cuota todavía no se cobró.
 * Lups no administra PAC — el cobro PAC lo gestiona FLUX directo (BCI). Cuando
 * Lups reporta `PAC` o el admin cierra como PAC, se muestra "PAC" a secas.
 */
const formatMechanism = (mechanism: PaymentMechanism | null, lupsPaymentMethod: string | null): string | null => {
  if (mechanism) return MECHANISM_LABELS[mechanism];
  if (!lupsPaymentMethod) return null;
  const upper = lupsPaymentMethod.toUpperCase();
  if (upper === 'PAC') return 'PAC';
  if (upper === 'PAT') return 'PAT';
  return lupsPaymentMethod;
};

const formatCLP = (n: number) => n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};
const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/**
 * Lista plana (tipo cartola de movimientos) de todas las cuotas en cobranza del comercio,
 * con próximo cobro, estado, mecanismo, intentos y último evento.
 */
export default function CollectionScheduleAllPanel() {
  const [rows, setRows] = useState<FlatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CollectionScheduleInstallment['collectionStatus'] | ''>('');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = getEndpointUrl('adelantos', 'collection-schedule-all');
        const schedules = await httpClient.get<CollectionSchedule[]>(url, { shouldCache: false });
        const flat = schedules.flatMap((s) => s.installments.map((inst) => ({ ...inst, advanceRequestId: s.advanceRequestId })));
        flat.sort((a, b) => {
          const aKey = a.nextAttemptAt ?? `${a.fechaObjetivo}T00:00:00`;
          const bKey = b.nextAttemptAt ?? `${b.fechaObjetivo}T00:00:00`;
          return aKey.localeCompare(bKey);
        });
        setRows(flat);
      } catch (err) {
        setError((err as Error).message || 'Error cargando programación');
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter && r.collectionStatus !== statusFilter) return false;
      const targetDate = r.nextAttemptAt ?? `${r.fechaObjetivo}T00:00:00`;
      if (fechaDesde && targetDate < `${fechaDesde}T00:00:00`) return false;
      if (fechaHasta && targetDate > `${fechaHasta}T23:59:59`) return false;
      return true;
    });
  }, [rows, statusFilter, fechaDesde, fechaHasta]);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border bg-card p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Programación de cobranza</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Cuotas de tus adelantos ya desembolsados, ordenadas por próxima fecha de cobro. Incluye cuotas en cobro y cobradas.
          </p>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CollectionScheduleInstallment['collectionStatus'] | '')}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="sent">En cobro</option>
            <option value="collected">Cobrada</option>
            <option value="abandoned">Abandonada</option>
          </select>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            placeholder="Desde"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            placeholder="Hasta"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Próximo cobro</th>
                <th className="py-2 pr-3">Adelanto · Cuota</th>
                <th className="py-2 pr-3 text-right">Monto</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Mecanismo</th>
                <th className="py-2 pr-3 text-right">Intentos</th>
                <th className="py-2 pr-3">Último evento</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">Cargando…</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-red-500">{error}</td>
                </tr>
              )}
              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted-foreground">
                    {rows.length === 0 ? 'Sin adelantos en curso con cuotas por cobrar.' : 'Sin cuotas para los filtros seleccionados.'}
                  </td>
                </tr>
              )}
              {!loading && !error && filtered.map((row) => {
                const status = STATUS_LABELS[row.collectionStatus];
                const lastEntry = row.timeline.length > 0 ? row.timeline[row.timeline.length - 1] : null;
                const rowKey = `${row.advanceRequestId}-${row.installmentDetailId}`;
                const hasTimeline = row.timeline.length > 0;
                const isExpanded = expanded.has(rowKey);
                return (
                  <Fragment key={rowKey}>
                    <tr className="border-t border-border align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {row.nextAttemptAt ? (
                          <>
                            <div className="font-medium">{formatDate(row.nextAttemptAt)}</div>
                            {row.nextAttemptReason === 'retry_send' && <div className="text-[11px] text-amber-600">Reintento de envío</div>}
                            {row.nextAttemptReason === 'scheduled' && <div className="text-[11px] text-muted-foreground">Al vencer la cuota</div>}
                            {row.nextAttemptReason === 'lups_internal' && <div className="text-[11px] text-muted-foreground">En proceso</div>}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <Link to={`/adelantos/detalle?id=${row.advanceRequestId}`} className="font-mono text-xs text-flux-primary hover:underline">
                          {row.advanceRequestId.slice(0, 12)}…
                        </Link>
                        <div className="text-xs text-muted-foreground mt-0.5">Cuota {row.numeroCuota}</div>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium whitespace-nowrap">{formatCLP(row.monto)}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${status.tone}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {(() => {
                          const label = formatMechanism(row.mechanism, row.lupsPaymentMethod);
                          return label ?? <span className="text-muted-foreground">—</span>;
                        })()}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {hasTimeline ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(rowKey)}
                            aria-expanded={isExpanded}
                            aria-controls={`${rowKey}-timeline`}
                            className="inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-foreground transition-colors hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          >
                            <span className="font-mono">{row.attempts}</span>
                            <span className={`material-symbols-outlined text-base transition-transform ${isExpanded ? 'rotate-180' : ''}`}>expand_more</span>
                          </button>
                        ) : (
                          <span className="font-mono">{row.attempts}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground max-w-xs truncate">
                        {lastEntry ? (
                          <>
                            <span className="font-medium text-foreground">{formatEventLabel(lastEntry.tipo)}</span>
                            <span className="ml-1 text-[11px]">· {formatDateTime(lastEntry.timestampEvento)}</span>
                          </>
                        ) : (
                          'Sin intentos todavía'
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasTimeline && (
                      <tr id={`${rowKey}-timeline`} className="border-t border-border bg-slate-50/60 dark:bg-slate-900/40">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="ml-2 border-l-2 border-slate-200 dark:border-slate-700 pl-4">
                            <p className="mb-2 text-[11px] uppercase tracking-wide font-bold text-muted-foreground">Historial de intentos</p>
                            <ol className="space-y-2">
                              {row.timeline.map((entry, idx) => {
                                const visual = OUTCOME_VISUAL[entry.outcome];
                                return (
                                  <li key={`${rowKey}-evt-${idx}`} className="flex items-start gap-2 text-xs">
                                    <span className={`material-symbols-outlined text-base shrink-0 mt-0.5 ${visual.tone}`}>{visual.icon}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-baseline gap-x-2">
                                        <span className="font-medium text-foreground">{formatEventLabel(entry.tipo)}</span>
                                        <span className="text-muted-foreground text-[11px]">{formatDateTime(entry.timestampEvento)}</span>
                                        {entry.instrument && (
                                          <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:text-slate-200">
                                            {entry.instrument}
                                          </span>
                                        )}
                                      </div>
                                      {entry.montoCobrado !== null && (
                                        <div className="mt-0.5 text-muted-foreground">
                                          Monto: <span className="font-mono text-foreground">{formatCLP(entry.montoCobrado)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
